/**
 * Export helpers for the generated QR.
 *
 * PNG is the print-ready asset: it is composed onto a white background with a quiet
 * zone and the share URL captioned underneath, so a printed code stays scannable and
 * remains usable if the camera fails. SVG is exported as the raw vector, for design and
 * print pipelines that place their own caption.
 */

const PNG_PADDING = 32;
const PNG_CAPTION_HEIGHT = 56;
const PNG_CAPTION_FONT =
  '16px system-ui, -apple-system, "Segoe UI", sans-serif';

export function buildQrFileName(shareName: string, extension: string): string {
  const slug = shareName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
  return `qr-${slug || 'compartido'}.${extension}`;
}

export function serializeQrSvg(svg: SVGSVGElement): string {
  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  return new XMLSerializer().serializeToString(clone);
}

export function downloadQrSvg(svg: SVGSVGElement, shareName: string): void {
  const blob = new Blob([serializeQrSvg(svg)], {
    type: 'image/svg+xml;charset=utf-8',
  });
  triggerDownload(
    URL.createObjectURL(blob),
    buildQrFileName(shareName, 'svg'),
    true,
  );
}

export function downloadQrPng(
  source: HTMLCanvasElement,
  shareName: string,
  shareUrl: string,
): void {
  const width = source.width + PNG_PADDING * 2;
  const height = source.height + PNG_PADDING * 2 + PNG_CAPTION_HEIGHT;

  const target = document.createElement('canvas');
  target.width = width;
  target.height = height;

  const context = target.getContext('2d');
  if (!context) {
    throw new Error('No se pudo generar la imagen del QR.');
  }

  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, width, height);
  context.drawImage(source, PNG_PADDING, PNG_PADDING);

  context.fillStyle = '#111827';
  context.font = PNG_CAPTION_FONT;
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(
    shareUrl,
    width / 2,
    source.height + PNG_PADDING + PNG_CAPTION_HEIGHT / 2,
    width - PNG_PADDING,
  );

  triggerDownload(
    target.toDataURL('image/png'),
    buildQrFileName(shareName, 'png'),
    false,
  );
}

function triggerDownload(
  href: string,
  fileName: string,
  revoke: boolean,
): void {
  const link = document.createElement('a');
  link.href = href;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  if (revoke) {
    URL.revokeObjectURL(href);
  }
}
